import React, { ReactElement } from "react";

import styled from "styled-components";

import {
  COVER_RATIO_HERO,
  StyledChip,
  StyledClamp,
  StyledColorCover,
  StyledFavicon,
  coverManagement,
  playControl,
  Placeholder,
} from "./shared";
import { UnfurlController, UnfurlData, formatFetchedAt } from "./unfurl";
import {
  ArrowRefreshIcon,
  InsertLinkIcon,
  WarningTriangleIcon,
} from "../../../components/Icons";
import { SkeletonLoader } from "../../../components/Loaders";
import {
  STACKED_CARD_COVER_RATIO as COVER_RATIO,
  STACKED_CARD_CONTROL_CLASS,
  StackedCardCover as Cover,
  StackedCardRow as StackRow,
} from "../../../components/StackedCard";
import { Body2, Caption1 } from "../../../components/Typography";
import { Typography } from "../../../components/Typography/Typography";

/* There is no global CSS reset in this Storybook, so a raw <button> falls back
   to the UA font (Arial) instead of the DS stack. Every button below is text, so
   they all need it. */
const CONTROL_FONT = `-apple-system, BlinkMacSystemFont, "Roboto",
    "Helvetica Neue", Arial, sans-serif`;

/**
 * LinkCard — a link preview as a LIFECYCLE, not two static layouts.
 *
 * Data climbs a progressive ladder (url → basic → full); the anatomy is
 * CONSTANT at every tier, so each slot swaps a skeleton for real content in
 * place and the card never reflows on upgrade. Four statuses layer on top:
 *   resolving — climbing the ladder, aspect + rows reserved by skeletons
 *   resolved  — the full card
 *   stale     — resolved, but past its cache window: shows `fetchedAt` and a
 *               re-unfurl affordance
 *   error     — the unfurl failed: favicon + raw URL + retry, NO media
 *
 * Compact ↔ expanded is a user-switchable DISPLAY LEVEL on the same link
 * entity (the data lives in the controller, so switching never refetches or
 * loses it) — not two separate card types.
 */

export type LinkDisplay = "compact" | "expanded";

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

const StyledDomainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 16px;
`;

const StyledChips = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 18px;
`;

/* The stale strip — a re-unfurl affordance with the fetch time. */
const StyledStaleStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-theme-600);

  > span {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const StyledReunfurl = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 3px 8px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--page-paper-main);
  color: var(--color-theme-800);
  font-family: ${CONTROL_FONT};
  font-size: 11px;
  cursor: pointer;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    background: var(--color-theme-200);
    border-color: var(--sc-selection-ring);
  }
`;

/* The error body — favicon + raw URL + retry, no media (per spec §5). It is a
   column row like any other: the padding comes from the role, so the error
   state lines up with the states it replaces instead of setting its own inset.
   Only the gap is widened — three stacked blocks, not four text lines. */
const StyledErrorRow = styled(StackRow)`
  gap: 10px;

  .err-head {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--red-alert);
  }

  .err-head svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  .err-url {
    display: flex;
    align-items: center;
    gap: 8px;
    word-break: break-all;
  }
`;

const StyledRetry = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--page-paper-main);
  color: var(--color-theme-800);
  font-family: ${CONTROL_FONT};
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: var(--color-theme-200);
    border-color: var(--sc-selection-ring);
  }
`;

/* The resolved cover. A production unfurl stores the OpenGraph image, so when
   the ladder returns one we render a real <img> (the Cover fill already gives
   it `object-fit: cover`); the fill colour is the fallback for links that carry
   no image at all. */
const resolvedCover = (data: UnfurlData): ReactElement =>
  data.heroSrc ? (
    <img src={data.heroSrc} alt="" />
  ) : (
    <StyledColorCover $color={data.heroColor ?? "#1D9E75"} />
  );

/* The expanded footer band — tinted, holds the title + domain under the hero. */
const StyledFooterTitle = styled(Body2)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* ------------------------------------------------------------------ */
/* The display-level switch — lives in the entity "…" slot             */
/* ------------------------------------------------------------------ */

const StyledDisplayToggle = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  background: var(--color-theme-200);
  color: var(--color-theme-700);
  font-family: ${CONTROL_FONT};
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--color-theme-300);
  }
`;

const DisplayToggle = ({
  display,
  onToggle,
}: {
  display: LinkDisplay;
  onToggle: () => void;
}): ReactElement => (
  <StyledDisplayToggle
    type="button"
    data-fixed
    className={STACKED_CARD_CONTROL_CLASS}
    aria-label="Switch display level"
    onClick={onToggle}
  >
    {display === "compact" ? "Expand ▾" : "Compact ▴"}
  </StyledDisplayToggle>
);

/* ------------------------------------------------------------------ */
/* LinkCard                                                            */
/* ------------------------------------------------------------------ */

export interface LinkCardProps {
  unfurl: UnfurlController;
  display?: LinkDisplay;
  onToggleDisplay?: () => void;
}

export const LinkCard = ({
  unfurl,
  display = "compact",
  onToggleDisplay,
}: LinkCardProps): ReactElement => {
  const { status, tier, data, fetchedAt, retry, reunfurl } = unfurl;

  // ── Error: favicon + raw URL + retry, no media ──────────────────
  if (status === "error") {
    return (
      <StyledErrorRow role="custom" direction="column">
        <div className="err-head">
          <WarningTriangleIcon />
          <Caption1 weight="bold" color="tertiary">
            Couldn’t load a preview
          </Caption1>
        </div>
        <div className="err-url">
          <StyledFavicon $bg="var(--color-theme-400)" data-fixed />
          <Caption1 color="secondary">{data.url}</Caption1>
        </div>
        <StyledRetry type="button" onClick={retry}>
          <ArrowRefreshIcon />
          <Typography as="span" variant="Caption 1">
            Retry
          </Typography>
        </StyledRetry>
      </StyledErrorRow>
    );
  }

  const hasBasic = tier === "basic" || tier === "full";
  const hasFull = tier === "full";
  /* The display level belongs to the host, so the switch only appears when a
     host actually owns one. A surface that fixes the card at one level (the
     whiteboard pins it to expanded) gets no dead control. */
  const toggle = onToggleDisplay ? (
    <DisplayToggle display={display} onToggle={onToggleDisplay} />
  ) : null;

  // ── Expanded: hero + tinted footer band (title + domain) ────────
  if (display === "expanded") {
    return (
      <>
        <Cover
          aspectRatio={COVER_RATIO_HERO}
          centerSlot={hasFull && data.playable ? playControl : undefined}
          topRightSlot={coverManagement()}
        >
          {hasFull ? (
            resolvedCover(data)
          ) : (
            <SkeletonLoader style={{ width: "100%", height: "100%" }} />
          )}
        </Cover>
        <StackRow
          role="footer"
          direction="column"
          background="var(--color-theme-200)"
        >
          <StyledDomainRow>
            {hasBasic ? (
              <StyledFavicon $bg={data.faviconColor ?? "#0F6E56"} data-fixed />
            ) : (
              <SkeletonLoader
                data-fixed
                style={{ width: 16, height: 16, borderRadius: 4 }}
              />
            )}
            {hasBasic ? (
              <Caption1 color="tertiary">{data.domain}</Caption1>
            ) : (
              <Caption1 color="tertiary">{data.url}</Caption1>
            )}
            <span style={{ flex: "1 1 auto" }} />
            {toggle}
          </StyledDomainRow>
          {hasBasic ? (
            <StyledFooterTitle weight="bold">{data.title}</StyledFooterTitle>
          ) : (
            <SkeletonLoader style={{ height: 14, width: "90%" }} />
          )}
          {hasFull ? (
            <Caption1 color="tertiary">{data.description}</Caption1>
          ) : (
            <SkeletonLoader style={{ height: 10, width: "70%" }} />
          )}
        </StackRow>
        {status === "stale" ? (
          <StackRow role="meta">
            <StaleStrip fetchedAt={fetchedAt} onReunfurl={reunfurl} />
          </StackRow>
        ) : null}
      </>
    );
  }

  // ── Compact: cover + favicon/domain + title + chips ─────────────
  return (
    <>
      <Cover
        aspectRatio={COVER_RATIO}
        centerSlot={hasFull && data.playable ? playControl : undefined}
      >
        {hasFull ? (
          resolvedCover(data)
        ) : (
          <SkeletonLoader style={{ width: "100%", height: "100%" }} />
        )}
      </Cover>

      <StackRow role="custom">
        <StyledDomainRow>
          {hasBasic ? (
            <StyledFavicon $bg={data.faviconColor ?? "#0F6E56"} data-fixed />
          ) : (
            <SkeletonLoader
              data-fixed
              style={{ width: 16, height: 16, borderRadius: 4 }}
            />
          )}
          {hasBasic ? (
            <Caption1 color="tertiary">{data.domain}</Caption1>
          ) : (
            <Caption1 color="tertiary">{data.url}</Caption1>
          )}
          <span style={{ flex: "1 1 auto" }} />
          {toggle}
        </StyledDomainRow>
      </StackRow>

      <StackRow role="title">
        {hasBasic ? (
          <StyledClamp>{data.title}</StyledClamp>
        ) : (
          <div style={{ width: "100%" }}>
            <SkeletonLoader style={{ height: 14, width: "92%" }} />
            <SkeletonLoader
              style={{ height: 14, width: "60%", marginTop: 6 }}
            />
          </div>
        )}
      </StackRow>

      <StackRow role="custom">
        <StyledChips>
          {hasFull ? (
            <>
              {data.kind ? (
                <StyledChip data-fixed>{data.kind}</StyledChip>
              ) : null}
              {data.duration ? (
                <StyledChip data-fixed>{data.duration}</StyledChip>
              ) : null}
            </>
          ) : (
            <SkeletonLoader
              style={{ height: 18, width: 96, borderRadius: 10 }}
            />
          )}
        </StyledChips>
      </StackRow>

      {status === "stale" ? (
        <StackRow role="meta">
          <StaleStrip fetchedAt={fetchedAt} onReunfurl={reunfurl} />
        </StackRow>
      ) : null}
    </>
  );
};

/* The stale affordance — fetch time + a manual re-unfurl button. */
const StaleStrip = ({
  fetchedAt,
  onReunfurl,
}: {
  fetchedAt: number | null;
  onReunfurl: () => void;
}): ReactElement => (
  <StyledStaleStrip>
    <Typography as="span" variant="Caption 1" color="tertiary">
      Preview may be out of date · fetched {formatFetchedAt(fetchedAt)}
    </Typography>
    <StyledReunfurl type="button" onClick={onReunfurl}>
      <ArrowRefreshIcon />
      Re-unfurl
    </StyledReunfurl>
  </StyledStaleStrip>
);

/* ---- skeleton ---- */

/* Mirrors the compact anatomy row for row (this is the `resolving` render at
   tier `url`), so a resolving link never reflows as it climbs the ladder. */
export const LinkSkeleton = (): ReactElement => (
  <>
    <Cover aspectRatio={COVER_RATIO}>
      <SkeletonLoader style={{ width: "100%", height: "100%" }} />
    </Cover>
    <StackRow role="custom">
      <StyledDomainRow>
        <SkeletonLoader
          data-fixed
          style={{ width: 16, height: 16, borderRadius: 4 }}
        />
        <SkeletonLoader style={{ height: 10, width: "40%" }} />
      </StyledDomainRow>
    </StackRow>
    <StackRow role="title">
      <div style={{ width: "100%" }}>
        <SkeletonLoader style={{ height: 14, width: "92%" }} />
        <SkeletonLoader style={{ height: 14, width: "60%", marginTop: 6 }} />
      </div>
    </StackRow>
    <StackRow role="custom">
      <StyledChips>
        <SkeletonLoader style={{ height: 18, width: 96, borderRadius: 10 }} />
      </StyledChips>
    </StackRow>
  </>
);

/* ---- placeholder ---- */

export const LinkPlaceholder = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement>
): ReactElement => (
  <Placeholder icon={<InsertLinkIcon />} label="Add a link" {...props} />
);
